export interface PaletteEntry {
  index:number; name:string; r:number; g:number; b:number; a:number; family:string;
  /** Legacy import aliases. New registries and exports use explicit RGBA channels. */
  hex?:string; alpha?:number;
}
export interface AssetPalette { id:string; name:string; description?:string; colors:PaletteEntry[]; builtIn?:boolean }
export interface AssetFrame {
  id:string; name:string; durationMs:number; pixels:number[]; rgba?:number[];
  /** Compact transport/cache form. Repositories hydrate these before runtime or Studio use. */
  pixelsRle?:number[]; rgbaRle?:number[];
  /** Pixel-run RGBA transport: [count, r, g, b, a]. */
  rgbaRuns?:number[];
}
export interface AssetBounds { x:number; y:number; w:number; h:number }
export interface AssetAnchor { id:string; name:string; type:string; x:number; y:number }
export interface AssetState { id:string; name:string; frameId:string }
export type AssetAnimationPlayback='loop'|'progress'|'hold';
export type AssetAnimationPhase='seeded'|'synchronized';
export interface AssetAnimationKeyframe { id:string; frameId:string; durationMs:number }
export interface AssetAnimation {
  id:string; name:string; state:string; variant?:string;
  playback:AssetAnimationPlayback; speed:number; phase:AssetAnimationPhase;
  keyframes:AssetAnimationKeyframe[];
}
export interface AssetVariant { id:string; name:string; frameId:string; weight:number; context:string; allowDecorators:boolean }
export type EnvironmentLayer='far-background'|'background'|'world-behind'|'world-front'|'foreground';
export interface EnvironmentElement {
  id:string; assetId:string; layer:EnvironmentLayer; x:number; y:number;
  scaleX?:number; scaleY?:number; rotationDeg?:number; opacity?:number; zIndex?:number;
  repeatX?:number; repeatY?:number; gapX?:number; gapY?:number;
  parallaxX?:number; parallaxY?:number; variant?:string; animationState?:string;
  /** Presentation-only motion remains theme data rather than renderer-specific drawing code. */
  animationEnabled?:boolean; animationOffsetMs?:number; spinDegPerSecond?:number; pivotX?:number; pivotY?:number;
  visible?:boolean;
}
export interface EnvironmentComposition {
  schema:1; width:number; height:number; backgroundColor?:string;
  elements:EnvironmentElement[];
}
export type EnvironmentPlacementPreset=Omit<EnvironmentElement,'id'|'assetId'>;
export type EnvironmentDecoratorPreset=Omit<AssetDecorator,'id'|'assetId'>;
export interface AssetDecorator {
  id:string; assetId:string; rule?:string; chance?:number; layer?:string;
  offsetX?:[number,number]; offsetY?:[number,number]; minSpacing?:number; overflow?:boolean;
  /** Optional authored-placement constraints. Older decorators remain valid. */
  allowedHosts?:string[]; contexts?:string[]; density?:number; seedSalt?:number;
  /** Deterministic legacy-grid placement preserves authored theme correlations. */
  placementMode?:'seeded-random'|'legacy-stable-art'|'coordinate-modulo';
  placementSalt?:number; placementMin?:number; placementMax?:number;
  coordinatePeriod?:number; coordinateRemainder?:number;
}
export interface CharacterColorway { id:string; name:string; publishToGame?:boolean; starter?:boolean; goldPrice?:number; skin?:string; hair?:string; primary?:string; secondary?:string; accent?:string }
export interface CharacterAssetMeta {
  rig?:string; slot?:string; bodyType?:string; equipLayer?:number; sku?:string; publishToGame?:boolean;
  availableInCustomizer?:boolean; starterItem?:boolean; goldPrice?:number;
  sourceRegions?:Record<string,string>; colorways?:CharacterColorway[];
  /** Procedural silhouette/detail variant consumed by the Dex v8.24 renderer. */
  shape?:string; collection?:string; description?:string;
}
export type FurnitureInteractionType='none'|'edit-home'|'character-shop'|'toggle'|'animate'|'sit'|'lie'|'open-vault'|'water';
export interface FurnitureAssetMeta {
  /** Stable gameplay key used by saves, the shop and semantic sound bindings. */
  furnitureKey:string; label:string; description?:string; shopEnabled:boolean; goldPrice:number;
  /** Exact occupied logical cells relative to the placement anchor. */
  footprintTiles:Array<[number,number]>; supportRule:'floor'|'wall'|'none';
  interactionType:FurnitureInteractionType; interactionStateKey?:'on'|'open'|'watered'; animationState?:string; animationDurationTicks?:number;
  playerPose?:'sit'|'lie'; readOnlyLocked?:boolean;
  /** Furniture is normally behind Dex; explicitly authored foreground art may opt in. */
  renderLayer:'world-behind'|'world-front'; achievement?:{track:string;tier:string}|null;
}
export type SoundBehaviorType='one-shot'|'reaction'|'device-loop'|'activity-loop'|'ambient';
export type SoundBus='world'|'ui'|'ambient';
export interface AudioClipRecord {id:string;filename:string;mimeType:string;durationMs:number;byteSize:number;weight:number;url?:string}
export interface SoundAssetMeta {
  eventKey:string;behaviorType:SoundBehaviorType;bus:SoundBus;baseVolume:number;pitchVariance:number;
  spatialRangeTiles:number;cooldownMs:number;mergeRadiusTiles:number;maxVoices:number;
  scaleVolumeWithIntensity:boolean;scalePitchWithIntensity:boolean;variants:AudioClipRecord[];
  startClip:AudioClipRecord|null;loopClip:AudioClipRecord|null;stopClip:AudioClipRecord|null;
  loopFadeInMs:number;loopFadeOutMs:number;preload?:boolean;
}
export type MaterialTextureMode='solid'|'coordinate-noise'|'animated-noise'|'wisps';
export type MaterialSurfacePattern='none'|'strip'|'broken-strip'|'foam'|'flecks';
export interface MaterialMicroSprite {
  width:number;height:number;
  /** Row-major palette-role indices: 0 transparent, 1 primary, 2 highlight, 3 shadow. */
  pixels:number[];
}
export interface MaterialParticleVisual {
  enabled:boolean;colour:string;highlightColour?:string;density:number;rate:number;opacity:number;
  sprite:MaterialMicroSprite;
}
export interface MaterialOverlayVisual {
  id:string;name:string;enabled:boolean;colour:string;opacity:number;density:number;rate:number;
  placement:'all'|'surface'|'underside';pattern:'flecks'|'streaks'|'cells'|'wisps';
}
export interface MaterialVisualProfile {
  schema:1;materialKey:string;label:string;
  colours:{body:string[];shadow:string;highlight:string;surface:string[];accent:string;hot:string;cold:string};
  opacity:number;
  texture:{mode:MaterialTextureMode;scale:number;density:number;rate:number;seedSalt:number;variants:string[]};
  surface:{enabled:boolean;height:number;pattern:MaterialSurfacePattern;colours:string[];density:number;rate:number};
  particles:{bubble:MaterialParticleVisual;spark:MaterialParticleVisual;droplet:MaterialParticleVisual};
  crust:{enabled:boolean;colours:string[];density:number;rate:number;sprite:MaterialMicroSprite};
  glow:{enabled:boolean;colour:string;intensity:number;radius:number;pulseRate:number};
  overlays:MaterialOverlayVisual[];
}
export interface AssetMetadata {
  theme?:string; parentTheme?:string; tags?:string[];
  logicalBounds?:AssetBounds; collision?:AssetBounds & {
    type?:string;
    /** Rectangle collision remains supported; pixel-mask uses per-frame 0/1 cells. */
    mode?:'bounds'|'pixel-mask'; autoFit?:boolean; frameMasks?:Record<string,number[]>;
  }; origin?:{x:number;y:number};
  overflow?:{allowed?:boolean;layerMode?:string}; decorators?:AssetDecorator[]; anchors?:AssetAnchor[];
  states?:AssetState[]; animations?:AssetAnimation[]; variants?:AssetVariant[]; variantStrategy?:{mode?:string;seedSalt?:number;clusterBias?:number;breaks?:number[];coordinatePeriod?:number;coordinateRemainder?:number};
  paletteId?:string; paletteSubset?:number[]; footprint?:{w:number;h:number}; layer?:string; character?:CharacterAssetMeta;
  furniture?:FurnitureAssetMeta;
  effects?:{shine?:{enabled?:boolean;periodMs?:number;colour?:string;size?:number}};
  sound?:SoundAssetMeta;
  /** Theme-scoped art inputs for a procedurally simulated/rendered material. */
  materialVisualProfile?:MaterialVisualProfile;
  /** Theme-owned scene graph. All referenced elements resolve through their Live asset versions. */
  environment?:EnvironmentComposition;
  /** Bundled theme-pack authoring presets. Themes copy these into their owned composition/assignment records. */
  environmentPlacements?:EnvironmentPlacementPreset[];
  environmentDecoratorPreset?:EnvironmentDecoratorPreset;
  /** Visual assets bind behaviors to semantic events, never filenames. */
  soundBindings?:Record<string,string>;
  provenance?:unknown;
}
export interface AssetVersion {
  id:string; number:number; createdAt:string; updatedAt:string; status:'live'|'draft'; width:number;height:number;
  frames:AssetFrame[]; note?:string; metadata:AssetMetadata;
}
export interface RWAsset { id:string; name:string; category:string; liveVersionId:string|null; versions:AssetVersion[]; createdAt:string }
export interface AssetRegistry {
  schema:number; paletteId:string; palettes?:AssetPalette[]; assets:RWAsset[];
  /** Ephemeral upgrade report; custom Live forks are preserved but never hidden. */
  migrationWarnings?:Array<{assetId:string;assetName:string;canonicalVersionId:string;customVersionId:string;message:string}>;
}
export interface RuntimeAssetPayload { registry:AssetRegistry; palette:PaletteEntry[] }

const byte=(value:unknown,fallback=0)=>Math.max(0,Math.min(255,Number.isFinite(Number(value))?Math.round(Number(value)):fallback));
const hexChannels=(value:unknown):[number,number,number,number?]=>{const match=String(value||'').match(/^#?([0-9a-f]{6})([0-9a-f]{2})?$/i);if(!match)return[0,0,0];return[parseInt(match[1].slice(0,2),16),parseInt(match[1].slice(2,4),16),parseInt(match[1].slice(4,6),16),match[2]?parseInt(match[2],16):undefined]};
const hslToRgb=(h:number,s:number,l:number):[number,number,number]=>{s/=100;l/=100;const c=(1-Math.abs(2*l-1))*s,x=c*(1-Math.abs((h/60)%2-1)),m=l-c/2;let r=0,g=0,b=0;if(h<60)[r,g,b]=[c,x,0];else if(h<120)[r,g,b]=[x,c,0];else if(h<180)[r,g,b]=[0,c,x];else if(h<240)[r,g,b]=[0,x,c];else if(h<300)[r,g,b]=[x,0,c];else[r,g,b]=[c,0,x];return[Math.round((r+m)*255),Math.round((g+m)*255),Math.round((b+m)*255)]};
const entry=(index:number,name:string,rgb:[number,number,number],family:string,a=255):PaletteEntry=>({index,name,r:rgb[0],g:rgb[1],b:rgb[2],a,family});

export function normalizePaletteEntry(value:Partial<PaletteEntry>&{rgba?:number[]},index:number):PaletteEntry{
  const legacy=hexChannels(value.hex),rgba=Array.isArray(value.rgba)?value.rgba:[];
  const hasExplicit=['r','g','b'].every(channel=>Number.isFinite(Number(value[channel as 'r'|'g'|'b'])));
  const r=byte(hasExplicit?value.r:(rgba[0]??legacy[0])),g=byte(hasExplicit?value.g:(rgba[1]??legacy[1])),b=byte(hasExplicit?value.b:(rgba[2]??legacy[2]));
  const alphaSource=value.a??rgba[3]??value.alpha??legacy[3]??255;
  return{index,name:String(value.name||`COLOUR_${index}`),r,g,b,a:index===0?0:byte(alphaSource,255),family:String(value.family||'custom')};
}

export function buildMasterPalette():PaletteEntry[]{
  const palette:PaletteEntry[]=[entry(0,'TRANSPARENT',[0,0,0],'system',0)];
  for(let i=1;i<=31;i++){const l=Math.round(4+(i-1)*(92/30));palette.push(entry(i,`NEUTRAL_${String(i).padStart(2,'0')}`,hslToRgb(220,7,l),'neutral'))}
  const families=[
    {family:'earth',name:'EARTH',hue:28,sat:[26,66],slots:32},{family:'metal',name:'METAL',hue:42,sat:[8,40],slots:32},
    {family:'nature',name:'NATURE',hue:118,sat:[24,62],slots:32},{family:'cool',name:'COOL',hue:205,sat:[32,72],slots:32},
    {family:'warm',name:'WARM',hue:8,sat:[42,82],slots:32},{family:'character',name:'CHAR',hue:318,sat:[18,58],slots:32}
  ];
  for(const f of families)for(let i=0;i<f.slots;i++){const row=Math.floor(i/8),col=i%8,hueShift=(col-3.5)*4,sat=f.sat[0]+row*((f.sat[1]-f.sat[0])/3),light=18+col*8.5;palette.push(entry(palette.length,`${f.name}_${String(i+1).padStart(2,'0')}`,hslToRgb((f.hue+hueShift+360)%360,sat,Math.min(78,light)),f.family))}
  for(const [name,hue] of [['ACID',84],['ELECTRIC',184],['ICE',192],['WATER',210],['LAVA',18],['FIRE',34],['STEAM',202],['TOXIC',112]] as const)for(let i=0;i<3;i++)palette.push(entry(palette.length,`${name}_${i+1}`,hslToRgb(hue,76-i*8,32+i*20),'effect'));
  ['#ff00ff','#00ffff','#ffff00','#ff3b30','#34c759','#0a84ff','#ffffff','#000000'].forEach((hex,i)=>palette.push(entry(palette.length,`RESERVED_${i+1}`,hexChannels(hex).slice(0,3) as [number,number,number],'system')));
  if(palette.length!==256)throw new Error(`Master palette must contain 256 entries; found ${palette.length}`);
  return palette;
}
export const MASTER_PALETTE=buildMasterPalette();
export function registryPalette(registry:AssetRegistry):PaletteEntry[]{const colors=registry.palettes?.find(p=>p.id===registry.paletteId)?.colors;if(!colors?.length)return MASTER_PALETTE;return colors.map((color,index)=>normalizePaletteEntry(color,index))}
